"use strict";
// Copyright © Veeam Software Group GmbH.
Object.defineProperty(exports, "__esModule", { value: true });
exports.useUserAfkContext = exports.UserAfkProvider = exports.UserAfkContext = exports.DEFAULT_TRACKING_DELAY = void 0;
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const core_1 = require("@veeam/core");
const noop_1 = require("../../../helpers/utils/noop");
const BroadcastChannel_1 = require("./BroadcastChannel");
const USER_AFK_INTERRUPT = 'userAFKInterrupt';
const AUDIT_TIME_BROADCAST_MESSAGE = 1000;
exports.DEFAULT_TRACKING_DELAY = 15 * 60 * 1000;
const eventTypes = [
    'auxclick',
    'blur',
    'click',
    'contextmenu',
    'dblclick',
    'drag',
    'dragend',
    'dragenter',
    'dragexit',
    'dragleave',
    'dragover',
    'dragstart',
    'drop',
    'focus',
    'focusin',
    'focusout',
    'gotpointercapture',
    'input',
    'keydown',
    'keypress',
    'keyup',
    'lostpointercapture',
    'mousedown',
    'mouseenter',
    'mouseleave',
    'mousemove',
    'mouseout',
    'mouseover',
    'mouseup',
    'pause',
    'play',
    'playing',
    'pointercancel',
    'pointerdown',
    'pointerenter',
    'pointerleave',
    'pointermove',
    'pointerout',
    'pointerover',
    'pointerup',
    'progress',
    'resize',
    'scroll',
    'touchcancel',
    'touchend',
    'touchmove',
    'touchstart',
    'transitioncancel',
    'transitionend',
    'transitionrun',
    'transitionstart',
    'wheel',
];
exports.UserAfkContext = react_1.createContext({
    activate: noop_1.noop,
    deactivate: noop_1.noop,
    suppress: noop_1.noop,
    unsuppress: noop_1.noop,
});
let unsubscribeCallbacks = [];
let bChannel;
const addEventsListeners = (handler) => {
    unsubscribeCallbacks = [];
    bChannel = new BroadcastChannel_1.BroadcastChannel('BroadcastChannel');
    const messageSubject = new rxjs_1.Subject();
    const eventHandler = (event) => {
        messageSubject.next({ message: event, fromBroadcast: true });
    };
    eventTypes.forEach((eventType) => {
        window.document.addEventListener(eventType, eventHandler, true);
        unsubscribeCallbacks.push(() => {
            window.document.removeEventListener(eventType, eventHandler, true);
        });
    });
    bChannel.onmessage = (messageEvent) => {
        if (messageEvent.data === USER_AFK_INTERRUPT) {
            messageSubject.next({ message: messageEvent });
        }
    };
    const subs = messageSubject
        .pipe(operators_1.auditTime(AUDIT_TIME_BROADCAST_MESSAGE))
        .subscribe(({ message, fromBroadcast }) => {
        handler(message);
        if (fromBroadcast) {
            bChannel.postMessage(USER_AFK_INTERRUPT);
        }
    });
    unsubscribeCallbacks.push(() => {
        bChannel.close();
    }, () => {
        subs.unsubscribe();
    });
};
const removeEventsListeners = () => {
    var _a;
    while (unsubscribeCallbacks.length) {
        (_a = unsubscribeCallbacks.pop()) === null || _a === void 0 ? void 0 : _a();
    }
};
const UserAfkProvider = ({ initialActiveState, initialTimeout = exports.DEFAULT_TRACKING_DELAY, onCallback, children, }) => {
    const { getErrorListener } = core_1.useAppContext();
    const [state, setState] = react_1.useState({
        timeout: initialTimeout,
        isActive: initialActiveState,
        isSuppressed: false,
    });
    react_1.useEffect(() => {
        const subs = new rxjs_1.Subscription();
        if (state.isActive && state.timeout > 0) {
            subs.add(rxjs_1.merge(rxjs_1.fromEventPattern(addEventsListeners, removeEventsListeners), rxjs_1.timer(0, AUDIT_TIME_BROADCAST_MESSAGE).pipe(operators_1.filter(() => state.isSuppressed), operators_1.tap(() => {
                bChannel.postMessage(USER_AFK_INTERRUPT);
            })))
                .pipe(operators_1.startWith(true), operators_1.debounceTime(state.timeout <= AUDIT_TIME_BROADCAST_MESSAGE
                ? AUDIT_TIME_BROADCAST_MESSAGE * 2
                : state.timeout), operators_1.take(1))
                .subscribe(() => {
                onCallback();
            }, (err) => {
                getErrorListener().onCatchError(err);
            }));
        }
        return () => {
            subs.unsubscribe();
        };
    }, [state.isActive, state.isSuppressed, state.timeout]);
    return react_1.createElement(exports.UserAfkContext.Provider, {
        value: {
            activate: () => setState((prevState) => (Object.assign(Object.assign({}, prevState), { isActive: true }))),
            deactivate: () => setState((prevState) => (Object.assign(Object.assign({}, prevState), { isActive: false }))),
            suppress: () => setState((prevState) => (Object.assign(Object.assign({}, prevState), { isSuppressed: true }))),
            unsuppress: () => setState((prevState) => (Object.assign(Object.assign({}, prevState), { isSuppressed: false }))),
        },
    }, children);
};
exports.UserAfkProvider = UserAfkProvider;
const useUserAfkContext = () => react_1.useContext(exports.UserAfkContext);
exports.useUserAfkContext = useUserAfkContext;
//# sourceMappingURL=UserAfkContext.js.map